import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Paper,
  Stack,
  TablePagination,
  Theme,
  useTheme,
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { observer } from "mobx-react";
import { Shipments } from "../../../../../models/Shipments";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { useStores } from "../../../use-stores";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { ShipmentStatusEnum } from "../../../../../models/Shipments/ShipmentStatus";
import ShipmentDetailsDrawer from "../../pages/shipments/ShipmentDetailsDrawer";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import {
  getModernPageColors,
  modernEmptyStateSx,
} from "../../../../../styles/modernListingPageSx";
import ShipmentListCard from "./ShipmentListCard";
import { canAddShipmentInventoryLines } from "../../../helpers/shipmentInventoryLines.helper";

interface ShipmentsTableProps {
  list: Shipments[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  allSelected: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (limit: number) => void;
  onDelete: (shipmentId: number) => void;
  onReturned: (shipmentId: number) => void;
  // onSelectedShipmentsChange: (selectedShipments: number[]) => void;
  clearShipments: boolean;
  onSortChange: (column: string) => void;
  orderBy: string;
  orderDirection: string;
  onRemovePacks: (shipmentId: number) => void;
  onAddPacks?: (shipmentId: number) => void;
  selectedShipmentIdsMap: Map<number, number>;
  selectAllItem: () => void;
  selectItem: (shipment: Shipments) => void;
  deSelectAllItem: () => void;
  deSelectItem: (shipment: Shipments) => void;
  updateShipmentStatus: (
    shipmentId: number,
    status: ShipmentStatusEnum,
  ) => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  onEditShipment: (shipmentId: number) => void;
}

const ShipmentsTable: React.FC<ShipmentsTableProps> = ({
  list,
  limit,
  page_no,
  total_count,
  // clearShipments,
  orderBy,
  orderDirection,
  allSelected,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
  onDelete,
  onReturned,
  selectedShipmentIdsMap,
  selectAllItem,
  selectItem,
  deSelectAllItem,
  deSelectItem,
  onAddPacks,
  updateShipmentStatus,
  onRefresh,
  refreshing = false,
  onEditShipment,
}) => {
  const [detailsDrawerShipmentId, setDetailsDrawerShipmentId] = useState<
    number | null
  >(null);
  const { userStore } = useStores();

  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);

  const sortOptions = useMemo(
    () => [
      { key: "id", label: "Shipment ID" },
      { key: "order_id", label: "Order ID" },
      { key: "created_date", label: "Placed On" },
      { key: "modified_date", label: "Modified On" },
    ],
    [],
  );

  const handleRequestSort = (property: string) => {
    onSortChange(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onPageChange(1);
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  const handleEdit = (shipmentId: number) => {
    try {
      onEditShipment(shipmentId);
    } catch (error) {
      console.error("Failed to open edit shipment", error);
    }
  };

  const handleMoveToNew = (shipmentId: number) => {
    try {
      updateShipmentStatus(shipmentId, ShipmentStatusEnum.NEW);
    } catch (error) {
      console.error("Failed to move shipment to New", error);
    }
  };

  const handleReturned = (shipmentId: number) => {
    onReturned(shipmentId);
  };

  const handleOpenShipmentDetails = (shipmentId: number) => {
    if (!userStore.hasPermission(EmployeeRoles.VIEW_SHIPMENTS)) return;
    setDetailsDrawerShipmentId(shipmentId);
  };

  const handleCloseShipmentDetails = () => {
    setDetailsDrawerShipmentId(null);
  };

  const canViewShipments = userStore.hasPermission(
    EmployeeRoles.VIEW_SHIPMENTS,
  );
  const canEditShipments = userStore.hasPermission(
    EmployeeRoles.EDIT_SHIPMENTS,
  );
  const canEditTerminalLines = userStore.hasPermission(
    EmployeeRoles.EDIT_ITEMS_FROM_TERMINAL_SHIPMENTS,
  );
  const canCancelShipments = userStore.hasPermission(
    EmployeeRoles.CANCEL_USER_PLACED_SHIPMENTS,
  );

  const canMoveToNew = (shipment: Shipments) =>
    shipment.status === ShipmentStatusEnum.RETURNED ||
    shipment.status === ShipmentStatusEnum.CANCELLED ||
    shipment.status === ShipmentStatusEnum.UNDELIVERED ||
    shipment.status === ShipmentStatusEnum.RETURN_REQUESTED;

  const areSomeSelected = (): boolean => {
    return (
      selectedShipmentIdsMap.size > 0 &&
      selectedShipmentIdsMap.size !== list.length
    );
  };

  return (
    <>
      <Paper
        sx={{
          marginTop: "0.6rem",
          borderRadius: "16px",
          border: `1px solid ${colors.border}`,
          boxShadow: colors.shadowSoft,
          overflow: "hidden",
          backgroundColor: colors.surface,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.5, sm: 2 },
            borderBottom: `1px solid ${colors.border}`,
            backgroundColor: colors.surfaceMuted,
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Checkbox
                  color="primary"
                  indeterminate={areSomeSelected()}
                  checked={
                    list.length > 0 &&
                    allSelected === false &&
                    selectedShipmentIdsMap.size === list.length
                  }
                  onClick={() => {
                    list.length > 0 &&
                    allSelected === false &&
                    selectedShipmentIdsMap.size === list.length
                      ? deSelectAllItem()
                      : selectAllItem();
                  }}
                />
                <Box>
                  <CustomTypography variant="heading7" fontWeight={700}>
                    Shipments
                  </CustomTypography>
                  <CustomTypography variant="caption" color={colors.subtitle}>
                    {selectedShipmentIdsMap.size > 0
                      ? `${selectedShipmentIdsMap.size} selected on this page`
                      : `${total_count} total shipments`}
                  </CustomTypography>
                </Box>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
            >
              {onRefresh && (
                <AppButton
                  label={refreshing ? "Refreshing…" : "Refresh"}
                  startIcon={<RefreshIcon />}
                  onClick={onRefresh}
                  disabled={refreshing}
                  customStyles={{
                    minWidth: "fit-content",
                    height: 34,
                    px: 1.5,
                    borderRadius: "999px",
                    color: colors.title,
                    backgroundColor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    "&:hover": {
                      backgroundColor: colors.surfaceMuted,
                    },
                  }}
                />
              )}
              <CustomTypography variant="caption" color={colors.subtitle}>
                Sort by
              </CustomTypography>
              {sortOptions.map((option) => {
                const isActive = orderBy === option.key;
                const directionLabel =
                  isActive && orderDirection.toLowerCase() === "asc"
                    ? "Asc"
                    : "Desc";

                return (
                  <AppButton
                    key={option.key}
                    label={
                      isActive
                        ? `${option.label}: ${directionLabel}`
                        : option.label
                    }
                    onClick={() => handleRequestSort(option.key)}
                    customStyles={{
                      minWidth: "fit-content",
                      height: 34,
                      px: 1.5,
                      borderRadius: "999px",
                      color: isActive ? colors.surface : colors.title,
                      backgroundColor: isActive
                        ? theme.palette.primary.main
                        : colors.surface,
                      border: `1px solid ${
                        isActive ? theme.palette.primary.main : colors.border
                      }`,
                      "&:hover": {
                        backgroundColor: isActive
                          ? theme.palette.primary.dark
                          : colors.surfaceMuted,
                      },
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            maxHeight: window.innerHeight - 340,
            overflowY: "auto",
            p: { xs: 1, sm: 1.5 },
            backgroundColor: colors.pageBg,
          }}
        >
          <Stack spacing={1.5}>
            {list?.map((shipment: Shipments) => {
              const isSelected =
                selectedShipmentIdsMap.get(shipment.id) !== undefined;

              return (
                <ShipmentListCard
                  key={shipment.id}
                  shipment={shipment}
                  isSelected={isSelected}
                  canView={canViewShipments}
                  canEdit={canEditShipments}
                  canCancel={canCancelShipments}
                  showMoveToNew={canMoveToNew(shipment)}
                  showAddItem={
                    Boolean(onAddPacks) &&
                    canAddShipmentInventoryLines({
                      status: shipment.status,
                      hasEditShipments: canEditShipments,
                      hasEditTerminalLines: canEditTerminalLines,
                    })
                  }
                  onToggleSelect={() => {
                    if (selectedShipmentIdsMap.get(shipment.id)) {
                      deSelectItem(shipment);
                    } else {
                      selectItem(shipment);
                    }
                  }}
                  onView={() => handleOpenShipmentDetails(shipment.id)}
                  onEdit={() => handleEdit(shipment.id)}
                  onAddItem={
                    onAddPacks ? () => onAddPacks(shipment.id) : undefined
                  }
                  onMarkReturned={() => handleReturned(shipment.id)}
                  onMoveToNew={() => handleMoveToNew(shipment.id)}
                  onDelete={() => onDelete(shipment.id)}
                />
              );
            })}
          </Stack>
        </Box>
        {list?.length === 0 && (
          <Box
            sx={{
              ...modernEmptyStateSx(theme),
              p: 4,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: colors.surface,
            }}
          >
            <CustomTypography
              variant="heading6"
              fontWeight={600}
              color={colors.subtitle}
            >
              No shipments match the current filters.
            </CustomTypography>
          </Box>
        )}
        {total_count > 0 && (
          <TablePagination
            rowsPerPageOptions={[30, 50, 100, 200, 500]}
            component="div"
            count={total_count}
            rowsPerPage={limit}
            page={page_no - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: colors.surface,
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  fontSize: "0.8rem",
                  color: colors.subtitle,
                },
            }}
          />
        )}
      </Paper>
      <ShipmentDetailsDrawer
        open={detailsDrawerShipmentId !== null}
        shipmentId={detailsDrawerShipmentId}
        onClose={handleCloseShipmentDetails}
      />
    </>
  );
};

export default observer(ShipmentsTable);
